import { API, Auth } from "aws-amplify";

export const getToken = async () => {
  // Auth.currentSession() will internally use the refresh token to mint a new
  // ID/access token when the current one is expired. We additionally guard
  // against clock skew / refresh-failure edge cases by checking the exp claim
  // and forcing a refresh via currentAuthenticatedUser().getSession() if the
  // token would be expired within the next 30 seconds.
  let session = await Auth.currentSession();
  let idToken = session.getIdToken();
  const nowSec = Math.floor(Date.now() / 1000);
  const exp = typeof idToken.getExpiration === 'function' ? idToken.getExpiration() : 0;
  if (!exp || exp - nowSec < 30) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      session = await new Promise((resolve, reject) => {
        user.getSession((err, refreshed) => (err ? reject(err) : resolve(refreshed)));
      });
      idToken = session.getIdToken();
    } catch (err) {
      throw new Error('Session expired; please sign in again.');
    }
  }
  // Using ID token instead of Access token to ensure we have email, amr (MFA claims), and auth_time
  return idToken.getJwtToken();
};

/**
 * Resolves the email for the current user from multiple potential sources.
 * This prevents 'email=undefined' API calls during transient auth states.
 */
const resolveAvailableEmail = async (loggedInUser) => {
  // 1. Check loggedInUser object directly (passed from parent)
  if (loggedInUser?.attributes?.email) {
    return loggedInUser.attributes.email;
  }

  // 2. Try current authenticated session
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken();
    if (idToken.payload?.email) {
      return idToken.payload.email;
    }
  } catch (err) {
    // Session might not be available
  }

  // 3. Last resort: current authenticated user object
  try {
    const user = await Auth.currentAuthenticatedUser();
    if (user?.attributes?.email) {
      return user.attributes.email;
    }
  } catch (err) {
    // User might not be fully logged in/available
  }

  return undefined;
};

export const converItemToAccountInfo = (data) => {
  if(data?.length){
    const convertedData = data.map((item) => {
      const newItem = {};
      for (const key in item) {
        if (item.hasOwnProperty(key)) {
          if (item[key] !== null && typeof item[key] === 'object' && ('S' in item[key] || 'N' in item[key] || 'BOOL' in item[key])) {
            if ('S' in item[key]) newItem[key] = item[key].S;
            else if ('N' in item[key]) newItem[key] = Number(item[key].N);
            else if ('BOOL' in item[key]) newItem[key] = item[key].BOOL;
            else newItem[key] = item[key]; // Fallback
          } else {
            newItem[key] = item[key];
          }
        }
      }
      return newItem;
    });
    
    return convertedData.map(item => {
      if (!item.request_status && item.status) {
        item.request_status = item.status;
      }
      return item;
    });
  }else{
    return[]
  }
};

export const getEmailAccountStatus = async (loggedInUser) => {
  
    try {
      const email = await resolveAvailableEmail(loggedInUser);
      if (!email || email === 'undefined') {
        console.warn("getEmailAccountStatus: No valid email found, skipping API call.");
        return null;
      }

      const token = await getToken();
      const apiName = "accountAppBackend";
      const timestamp = new Date().getTime();
      const path = `/account?email=${email}&t=${timestamp}`;
      const accountStatusInit = {
        body: {},
        headers: {
          Authorization: `Bearer ${token}`
        },
      };
      
      const accountList = await API.get(apiName, path, accountStatusInit)
        .then((response) => {
          
          return converItemToAccountInfo(response?.data?.Items);
        //   setIsAccountRequested(false);
          // setAccounts(converItemToAccountInfo(response.data.Items));
        })
        .catch((error) => {
          console.log("API.get", error.response);
          return null;
        });
        
        return accountList;
    } catch (error) {
      console.error("Error fetching account status:", error);
      return null;
    }
  };

// ---------------------------------------------------------------------------
// Session-management helpers (Addendum 3: overlap & race-condition fixes)
// ---------------------------------------------------------------------------

// Single source of truth for the auth-cookie domain. Both App.js (Amplify
// cookieStorage config) and RightPanel.js (sign-out cookie wipe) read this,
// so the two paths cannot drift apart again.
export const getCookieDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes("tempclass.com")) {
        return ".tempclass.com";
    }
    if (hostname.includes("tempcomputing.com")) {
        return ".tempcomputing.com";
    }
    return hostname;
};

// Per-tab dedupe for Auth.currentAuthenticatedUser({bypassCache:true}). Without
// this multiple components racing on session recovery can throttle Cognito.
let _currentAuthenticatedUserPromise = null;
export const getCurrentAuthenticatedUserDeduped = async () => {
    if (_currentAuthenticatedUserPromise) {
        return _currentAuthenticatedUserPromise;
    }
    _currentAuthenticatedUserPromise = Promise.race([
        Auth.currentAuthenticatedUser({ bypassCache: true }),
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Network timeout")), 8000)
        )
    ]).finally(() => {
        _currentAuthenticatedUserPromise = null;
    });
    return _currentAuthenticatedUserPromise;
};

// S4/S8: Cross-tab lock using the Web Locks API (auto-released on tab crash).
// Falls back to a localStorage-based lock when navigator.locks is unavailable
// (very old Safari). Always purges its own stale fallback locks on entry.
const LOCK_FALLBACK_PREFIX = "auth:submit:lock:";
const LOCK_FALLBACK_TTL_MS = 20000;

const purgeStaleFallbackLocks = () => {
    try {
        const now = Date.now();
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(LOCK_FALLBACK_PREFIX)) {
                const ts = Number(localStorage.getItem(key));
                if (Number.isNaN(ts) || now - ts >= LOCK_FALLBACK_TTL_MS) {
                    toRemove.push(key);
                }
            }
        }
        toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
        /* ignore */
    }
};

/**
 * Runs `task` while holding a cross-tab exclusive lock named `lockName`.
 * Uses navigator.locks when available (auto-released on tab crash).
 * Returns whatever `task` returns. Throws an "in-progress" error if the
 * lock cannot be acquired immediately.
 */
export const withCrossTabLock = async (lockName, task) => {
    const fullName = `auth:${lockName}`;

    if (typeof navigator !== "undefined" && navigator.locks && typeof navigator.locks.request === "function") {
        // ifAvailable:true returns immediately if another holder exists.
        return navigator.locks.request(fullName, { ifAvailable: true }, async (lock) => {
            if (!lock) {
                const err = new Error("A verification request is already in progress in another tab. Please wait.");
                err.name = "CrossTabLockBusy";
                throw err;
            }
            return task();
        });
    }

    // Fallback: localStorage lock with stale-entry purge on each acquire.
    purgeStaleFallbackLocks();
    const key = `${LOCK_FALLBACK_PREFIX}${lockName}`;
    const existing = localStorage.getItem(key);
    if (existing) {
        const ts = Number(existing);
        if (!Number.isNaN(ts) && Date.now() - ts < LOCK_FALLBACK_TTL_MS) {
            const err = new Error("A verification request is already in progress in another tab. Please wait.");
            err.name = "CrossTabLockBusy";
            throw err;
        }
    }
    localStorage.setItem(key, String(Date.now()));
    try {
        return await task();
    } finally {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
};

// S3: Cross-tab auth-event channel. Other tabs subscribe and react to SIGNED_OUT.
const AUTH_CHANNEL_NAME = "auth-events";
const AUTH_STORAGE_KEY = "auth:lastEvent";

let _authChannel = null;
const getAuthChannel = () => {
    if (_authChannel) return _authChannel;
    if (typeof BroadcastChannel === "function") {
        try {
            _authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
        } catch {
            _authChannel = null;
        }
    }
    return _authChannel;
};

export const broadcastAuthEvent = (type, detail = {}) => {
    const payload = { type, detail, ts: Date.now() };
    const ch = getAuthChannel();
    if (ch) {
        try { ch.postMessage(payload); } catch { /* ignore */ }
    }
    // Fallback (and belt-and-braces): write to localStorage so the
    // 'storage' event reaches other tabs even when BroadcastChannel is
    // unavailable or fails.
    try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
};

export const subscribeToAuthEvents = (handler) => {
    const ch = getAuthChannel();
    const onMessage = (event) => {
        try { handler(event.data); } catch { /* ignore */ }
    };
    const onStorage = (event) => {
        if (event.key !== AUTH_STORAGE_KEY || !event.newValue) return;
        try { handler(JSON.parse(event.newValue)); } catch { /* ignore */ }
    };
    if (ch) ch.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    return () => {
        if (ch) ch.removeEventListener("message", onMessage);
        window.removeEventListener("storage", onStorage);
    };
};

// S10: Per-session refresh-attempt marker keyed by Cognito sub so it survives
// route remounts within a tab but does not leak across users/sessions.
const REFRESH_FLAG_PREFIX = "auth:attrsRefreshed:";
export const hasRefreshedAttrsForSub = (sub) => {
    if (!sub) return false;
    try { return sessionStorage.getItem(`${REFRESH_FLAG_PREFIX}${sub}`) === "1"; } catch { return false; }
};
export const markRefreshedAttrsForSub = (sub) => {
    if (!sub) return;
    try { sessionStorage.setItem(`${REFRESH_FLAG_PREFIX}${sub}`, "1"); } catch { /* ignore */ }
};
